import React from "react";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";

// no lazy loading for auth pages to avoid flickering
const AuthLayout = React.lazy(
  () => import("@app/components/layouts/AuthLayout/AuthLayout")
);
import LoginPage from "@app/pages/LoginPage";
import MainLayout from "@app/components/layouts/main/MainLayout/MainLayout";
import RequireAuth from "@app/components/router/RequireAuth";
import { withLoading } from "@app/hocs/withLoading.hoc";
import UserPage from "@app/pages/admin/UserPage";
import CompanyApplicationPage from "@app/pages/admin/CompanyApplicationPage";
import ContactPage from "@app/pages/admin/ContactPage";
import DailyReportPage from "@app/pages/admin/DailyReportPage";



export const MEDICAL_DASHBOARD_PATH = "/medical-dashboard";
export const NFT_DASHBOARD_PATH = "/nft-dashboard";

// Admin Pages
const CompanyPage = React.lazy(() => import("@app/pages/admin/CompanyPage"));
const DepartmentPage = React.lazy(
  () => import("@app/pages/admin/DepartmentPage")
);
const EmployeePage = React.lazy(() => import("@app/pages/admin/employee/EmployeePage"));
const AttendanceRulePage = React.lazy(
  () => import("@app/pages/admin/schedule/AttendanceRulePage")
);
const AttendanceGroupPage = React.lazy(
  () => import("@app/pages/admin/schedule/AttendanceGroupPage")
);
const AttendanceRecordPage = React.lazy(
  () => import("@app/pages/admin/schedule/AttendanceRecordPage")
);
const AttendanceConfirmationPage = React.lazy(
  () => import("@app/pages/admin/schedule/AttendanceConfirmationPage")
);
const SalaryPage = React.lazy(() => import("@app/pages/admin/SalaryPage"));
const SalaryCategoryPage = React.lazy(() => import("@app/pages/admin/SalaryCategoryPage"));
const MessagePage = React.lazy(() => import("@app/pages/admin/MessagePage"));
const PermissionPage = React.lazy(
  () => import("@app/pages/admin/PermissionPage")
);
const RolePage = React.lazy(() => import("@app/pages/admin/RolePage"));
const LeaveApprovalPage = React.lazy(() => import("@app/pages/admin/LeaveApprovalPage"));

// Leave Types Pages
const LeaveTypesListPage = React.lazy(() => import("@app/pages/admin/LeaveTypesListPage"));

// Schedule Pages
const ScheduleListPage = React.lazy(() => import("@app/pages/admin/schedule/ScheduleListPage"));
const ScheduleFormPage = React.lazy(() => import("@app/pages/admin/schedule/ScheduleFormPage"));
const BatchSchedulePage = React.lazy(() => import("@app/pages/admin/schedule/BatchSchedulePage"));
const ScheduleCopyPage = React.lazy(() => import("@app/pages/admin/schedule/ScheduleCopyPage"));
const ScheduleCalendarPage = React.lazy(() => import("@app/pages/admin/schedule/ScheduleCalendarPage"));
const ShiftTypeManagePage = React.lazy(() => import("@app/pages/admin/schedule/ShiftTypeManagePage"));

// Daily Report Page
const DailyReport = withLoading(DailyReportPage);



const Error404Page = React.lazy(() => import("@app/pages/Error404Page"));
// const ApiDocPage = React.lazy(() => import("@app/pages/ApiDocPage"));

// Admin Components
const Company = withLoading(CompanyPage);
const Department = withLoading(DepartmentPage);
const Employee = withLoading(EmployeePage);
const User = withLoading(UserPage);
const CompanyApplication = withLoading(CompanyApplicationPage);
const AttendanceRule = withLoading(AttendanceRulePage);
const AttendanceGroup = withLoading(AttendanceGroupPage);
const AttendanceRecord = withLoading(AttendanceRecordPage);
const AttendanceConfirmation = withLoading(AttendanceConfirmationPage);
const Salary = withLoading(SalaryPage);
const SalaryCategory = withLoading(SalaryCategoryPage);
const Message = withLoading(MessagePage);
const Permission = withLoading(PermissionPage);
const Role = withLoading(RolePage);
const Contact = withLoading(ContactPage);
const LeaveApproval = withLoading(LeaveApprovalPage);



// Leave Types Components
const LeaveTypesList = withLoading(LeaveTypesListPage);

// Schedule Components
const ScheduleList = withLoading(ScheduleListPage);
const ScheduleForm = withLoading(ScheduleFormPage);
const BatchSchedule = withLoading(BatchSchedulePage);
const ScheduleCopy = withLoading(ScheduleCopyPage);
const ScheduleCalendar = withLoading(ScheduleCalendarPage);
const ShiftTypeManage = withLoading(ShiftTypeManagePage);



const Error404 = withLoading(Error404Page);
// const ApiDoc = withLoading(ApiDocPage);

const AuthLayoutFallback = withLoading(AuthLayout);

export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  );

  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={protectedLayout}>
          <Route index element={<Navigate to="/company" replace />} />
          <Route path="company" element={<Company />} />
          <Route path="department" element={<Department />} />
          <Route path="employee" element={<Employee />} />
          <Route path="User" element={<User />} />
          <Route path="companyApplication" element={<CompanyApplication />} />
       
          <Route path="/admin/salary" element={<Salary />} />
          <Route path="/admin/salaryCategories" element={<SalaryCategory />} />
          <Route path="messages" element={<Message />} />
          <Route path="contact" element={<Contact />} />
          <Route path="/admin/leaveApproval" element={<LeaveApproval />} />
          <Route path="/admin/leaveTypes" element={<LeaveTypesList />} />
        
          
          <Route path="schedule/list" element={<ScheduleList />} />
          <Route path="schedule/new" element={<ScheduleForm />} />
          <Route path="schedule/edit/:id" element={<ScheduleForm />} />
          <Route path="schedule/batch" element={<BatchSchedule />} />
          <Route path="schedule/copy" element={<ScheduleCopy />} />
          <Route path="schedule/calendar" element={<ScheduleCalendar />} />
          <Route path="schedule/shiftTypes" element={<ShiftTypeManage />} />
          <Route path="schedule/attendanceRules" element={<AttendanceRule />} />
          <Route path="schedule/attendanceGroups" element={<AttendanceGroup />} />
          <Route path="schedule/attendanceRecords" element={<AttendanceRecord />} />
          <Route path="schedule/attendanceConfirmations" element={<AttendanceConfirmation />} />

          <Route path="/admin/permissions" element={<Permission />} />
          <Route path="/admin/roles" element={<Role />} />
          <Route path="/admin/dailyReports" element={<DailyReport />} />

          {/* <Route path="docs" element={<ApiDoc />} /> */}
          <Route path="404" element={<Error404 />} />
        </Route>
        <Route path="/auth" element={<AuthLayoutFallback />}>
          <Route path="login" element={<LoginPage />} />
        </Route>
      </Routes>
    </HashRouter>
  );
};
